/**
 * NxFlashCards -- Tarjetas de memoria con volteo (flip).
 * Item pedagogico para capacitacion: muestra pregunta al frente,
 * respuesta al voltear. El usuario marca si sabia o no.
 * Compatible con learning_questions de la BD.
 *
 * @module presentation/shared/NxFlashCards
 * @version 1.0
 */

import { LearningApiClient } from '../../infrastructure/api/LearningApiClient.js';

export const NX_FLASHCARDS_VERSION = '1.0';

// Deuda 4: el escape local no cambiaba & ni " y perdia el 0; ahora es el compartido.
import { esc } from './texto.js';

/**
 * Build and mount flash cards engine.
 * @param {HTMLElement} containerEl
 * @param {Object} config
 * @param {string} [config.userId]
 * @param {string} [config.category]
 * @param {string} [config.context]
 * @returns {Promise<{destroy: Function}>}
 */
export async function buildFlashCards(containerEl, config = {}) {
  const uid = config.userId || 'anon';
  const context = config.context || 'flashcards';

  // Load questions from API
  let questions = [];
  try {
    const res = await LearningApiClient.getQuestions({ category: config.category, limit: 20, random: true });
    if (res.ok && res.data?.data?.length > 0) {
      questions = res.data.data.map(q => ({
        id: q.id,
        front: q.question_text || q.questionText,
        back: (typeof q.options === 'string' ? JSON.parse(q.options) : q.options)[q.correct_index ?? q.correctIndex],
        cat: q.category
      }));
    }
  } catch {}

  if (questions.length === 0) {
    containerEl.innerHTML = '<p class="empty-state">Sin tarjetas disponibles</p>';
    return { destroy() { containerEl.innerHTML = ''; } };
  }

  let idx = 0;
  let known = 0;
  let total = 0;
  let flipped = false;

  function render() {
    if (idx >= questions.length) idx = 0;
    const q = questions[idx];
    flipped = false;
    containerEl.innerHTML = `
      <div class="nx-fc-wrap">
        <div class="nx-fc-header">
          <span class="nss5-quiz-badge">Tarjetas de Memoria</span>
          <span class="nss5-quiz-score">${known}/${total} sabia</span>
        </div>
        <div class="nx-fc-card" id="nx-fc-card">
          <div class="nx-fc-front">${esc(q.front)}</div>
          <div class="nx-fc-back" hidden>${esc(q.back)}</div>
        </div>
        <div class="nx-fc-hint" id="nx-fc-hint">Toca la tarjeta para voltear</div>
        <div class="nx-fc-actions" id="nx-fc-actions" hidden>
          <button class="nx-fc-btn nx-fc-btn--no" data-fc-knew="no">No sabia</button>
          <button class="nx-fc-btn nx-fc-btn--yes" data-fc-knew="yes">Si sabia</button>
        </div>
        <div class="nss5-quiz-footer">
          <span class="nss5-quiz-cat">${q.cat === 'productividad' ? '\uD83D\uDCCB Productividad' : '\uD83D\uDCE3 Marketing'}</span>
          <span class="nss5-quiz-progress">Tarjeta ${total + 1}</span>
        </div>
      </div>`;

    containerEl.querySelector('#nx-fc-card').addEventListener('click', () => {
      if (flipped) return;
      flipped = true;
      const front = containerEl.querySelector('.nx-fc-front');
      const back = containerEl.querySelector('.nx-fc-back');
      front.hidden = true;
      back.hidden = false;
      containerEl.querySelector('#nx-fc-hint').hidden = true;
      containerEl.querySelector('#nx-fc-actions').hidden = false;
    });

    containerEl.querySelectorAll('[data-fc-knew]').forEach(btn => {
      btn.addEventListener('click', () => {
        total++;
        const knew = btn.dataset.fcKnew === 'yes';
        if (knew) known++;
        // Record to API
        if (q.id) {
          LearningApiClient.recordProgress({ userId: uid, questionId: q.id, isCorrect: knew, context }).catch(() => {});
        }
        idx++;
        render();
      });
    });
  }

  render();
  return { destroy() { containerEl.innerHTML = ''; } };
}
